import * as React from 'react';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import useLocalStorage from 'common/hooks/useLocalStorage';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import { scGray2 } from 'constants/systemColors';
import { difference, isEmpty, isEqual } from 'lodash';
import moment from 'moment/moment';
import { muiDataGridLocaleText } from 'services/i18n/mui';

import { CustomFooter } from 'components/pages/finance/shared/FinanseTableMUI/CustomFooter/CustomFooter';
import { CustomToolbar } from 'components/pages/finance/shared/FinanseTableMUI/CustomToolbar/CustomToolbar';
import { useSiteFinanceTableColumns } from 'components/pages/finance/SiteFinance/SiteFinanceTable/useSiteFinanceTableColumns';
import {
  addNewColumns,
  removeDeletedColumns,
  removeOutdatedFilters,
  removeOutdatedSorting
} from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/CustomViews/CustomViewsServices';

import { patientStatusMap } from '../../../patient-source/Patients/constants';
import { CustomRow } from '../../shared/FinanseTableMUI/FinanceTableMUI';
import { SiteFinanceTableContext } from '../SiteFinanceContext';

export const SiteFinanceTable = () => {
  const apiRef = useGridApiRef();
  const { tableData, firstLoading, allStudiesFms, userActiveRoleIsGlobal } = useContext(SiteFinanceTableContext);
  const userAbleToSeeARInfo = useMemo(() => {
    return allStudiesFms || userActiveRoleIsGlobal;
  }, [allStudiesFms, userActiveRoleIsGlobal]);
  const { columns, tableVersion, tableName } = useSiteFinanceTableColumns(userAbleToSeeARInfo);
  const [savedState, setSavedState] = useLocalStorage(`${tableName}_TABLE_CONFIGURATION`, {});

  useLayoutEffect(() => {
    const savedStateVersion = localStorage?.getItem(`${tableName}_TABLE_VERSION`);
    if (savedStateVersion !== tableVersion) {
      localStorage.removeItem(`${tableName}_TABLE_CONFIGURATION`);
      localStorage.removeItem(`${tableName}_TABLE_CUSTOM_VIEW`);
    }
  }, [tableName, tableVersion]);

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage && !firstLoading) {
      const currentState = apiRef.current.exportState();
      if (!isEqual(savedState, currentState)) {
        setSavedState(currentState);
        localStorage.setItem(`${tableName}_TABLE_VERSION`, tableVersion);
      }
    }
  }, [apiRef, firstLoading, savedState, setSavedState, tableName, tableVersion]);

  const defaultTableState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          eventNumber: false,
          investigators: false,
          patientId: false,
          adjustmentMemo: false,
          currentPatientStatus: false,
          eventPatientStatus: false,
          invoiceNumber: false,
          visitDate: false
        }
      }
    }),
    []
  );

  const migrateTableState = useCallback(
    state => {
      const standardOrderedFields = columns.map(col => col.field);
      const removedColumns = difference(state.columns.orderedFields, standardOrderedFields);
      const newColumns = difference(standardOrderedFields, state.columns.orderedFields);

      // Process view if there are new columns
      const savedViewWithNewColumns = !isEmpty(newColumns)
        ? addNewColumns({ value: state }, newColumns, standardOrderedFields, [], defaultTableState)
        : { value: state };
      // Process view if there are removed columns
      const savedViewWithoutRemovedColumns = !isEmpty(removedColumns)
        ? removeDeletedColumns(savedViewWithNewColumns, removedColumns)
        : savedViewWithNewColumns;

      // Process view if there are filter saved
      const savedViewWithoutOutdatedFilters = removeOutdatedFilters(savedViewWithoutRemovedColumns, columns);

      // Process view if there are sorting saved
      const { value } = removeOutdatedSorting(savedViewWithoutOutdatedFilters, standardOrderedFields);
      return value;
    },
    [columns, defaultTableState]
  );

  const initialStateValue = useMemo(() => {
    const savedStateVersion = localStorage?.getItem(`${tableName}_TABLE_VERSION`);
    if (!isEmpty(savedState) && savedStateVersion === tableVersion) {
      return migrateTableState(savedState);
    }
    return defaultTableState;
  }, [defaultTableState, migrateTableState, savedState, tableName, tableVersion]);

  const [initialState, setInitialState] = useState({ key: 0, value: initialStateValue });
  const [quickFilterExpanded, setQuickFilterExpanded] = useState(
    initialStateValue?.filter?.filterModel?.quickFilterValues?.length > 0
  );

  const savedStateRef = React.useRef(savedState);

  useEffect(() => {
    savedStateRef.current = savedState;
  }, [savedState]);

  useEffect(() => {
    const savedStateVersion = localStorage?.getItem(`${tableName}_TABLE_VERSION`);
    if (!isEmpty(savedStateRef.current) && savedStateVersion === tableVersion) {
      const normalizedSavedState = migrateTableState(savedStateRef.current);
      setInitialState(prevState => {
        return { key: prevState.key + 1, value: normalizedSavedState };
      });
    }
  }, [migrateTableState, tableName, tableVersion]);

  return (
    <>
      <DataGridPremium
        key={initialState.key}
        apiRef={apiRef}
        onStateChange={saveSnapshot}
        sx={{
          padding: '20px 40px 0 40px',
          border: 'none',
          '.MuiDataGrid-pinnedColumns, .MuiDataGrid-pinnedColumnHeaders': {
            backgroundColor: scGray2
          }
        }}
        getRowId={row => row.itemSiteId}
        rows={tableData}
        columns={columns}
        rowHeight={38}
        localeText={muiDataGridLocaleText({
          noRowsLabel: firstLoading ? 'Make selections to display results' : 'No results to display'
        })}
        disableRowSelectionOnClick
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
          row: CustomRow
        }}
        slotProps={{
          root: {
            'data-testid': 'site-finance-table'
          },
          columnsManagement: {
            sort: 'asc'
          },
          filterPanel: {
            columnsSort: 'asc'
          },
          toolbar: {
            'data-testid': 'finance-table-toolbar',
            localStorageTableName: tableName,
            migrateTableState,
            printOptions: { disableToolbarButton: true },
            excelOptions: { disableToolbarButton: true },
            csvOptions: {
              utf8WithBom: true,
              escapeFormulas: false
            },
            quickFilterProps: { expanded: quickFilterExpanded, onExpandedChange: setQuickFilterExpanded }
          },
          footer: {
            count: tableData.length
          }
        }}
        initialState={initialState.value}
        showToolbar
        disableAggregation
        disablePivoting
        disableRowGrouping
      />
    </>
  );
};

export function convertPatientStatus(statusId) {
  const status = patientStatusMap.find(status => status.id === statusId);
  return status ? status.name : null;
}

export const DefaultDateRenderCell = ({ date }) => (
  <div>{date ? moment.utc(date).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
);
